import {ContactInfoCard} from '../components/contact/ContactInfoCard.tsx';
import {ContactForm} from '../components/contact/ContactForm.tsx';
import {FAQAccordion} from '../components/contact/FAQAccordion.tsx';
import {Footer} from '../components/contact/Footer.tsx';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import styles from '../styles/contact.module.css';


export function ContactPage() {
  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      content: 'Hello@sfrays.com',
      description: 'We typically respond within 24 hours'
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: '+1 (314)-220-4289',
      description: 'Mon-Fri, 9AM - 6PM EST'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      content: 'SFRays LLC, 3324 Rue Royale Street, #1218, Saint Charles, MO 63301',
      description: 'Schedule an appointment first'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Monday - Friday: 9AM - 6PM',
      description: 'Closed on weekends'
    }
  ];

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <motion.section 
        className={styles.hero}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className={styles.heroGradient}></div>
        <div className={styles.heroContent}>
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <h1 className={styles.heroTitle}>Let's Build Something Amazing Together</h1>
            <p className={styles.heroSubtitle}>
              Whether you have a question, need support, or want to explore a partnership, 
              we're here to help turn your vision into reality.
            </p>
          </motion.div>
        </div>
        
        {/* Floating Orbs */}
        <motion.div 
          className={styles.orb1}
          animate={{ 
            y: [0, -20, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className={styles.orb2}
          animate={{ 
            y: [0, 20, 0],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{ 
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
      </motion.section>

      {/* Contact Section */}
      <section className={styles.contactSection}>
        <div className={styles.contentWrapper}>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={styles.sectionTitle}>Get in Touch</h2>
            <p className={styles.sectionSubtitle}>
              Choose the way that works best for you – we're always ready to connect
            </p>
          </motion.div>

          <div className={styles.twoColumnLayout}>
            {/* Left Column - Contact Info */}
            <motion.div 
              className={styles.contactInfoColumn}
              initial={{ x: -30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {contactInfo.map((info, index) => (
                <ContactInfoCard 
                  key={index}
                  icon={info.icon}
                  title={info.title}
                  content={info.content}
                  description={info.description}
                  delay={index * 0.1}
                />
              ))}
            </motion.div>

            {/* Right Column - Contact Form */}
            <motion.div
              initial={{ x: 30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.contentWrapper}>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
            <p className={styles.sectionSubtitle}>
              Quick answers to common questions about working with us
            </p>
          </motion.div>

          <FAQAccordion />
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
